import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DropzoneOptions, ErrorCode, FileRejection, useDropzone } from 'react-dropzone';

import { SkeletonItem } from 'app/components/DetailsPages/shared/styled';
import {
  ResourcesDetailInlineCTAContainer,
  ResourcesDetailInlineCTAIconContainer,
  ResourcesDetailInlineCTAImageContainer,
  ResourcesDetailInlineCTAName,
  ResourcesDetailInlineCTARemoveIconContainer,
  ResourcesDetailInlineCTAWrapper,
} from 'app/components/Resources/ResourceDetail/ResourcesDetailInlineCTA/styled';
import {
  ResourceCTAButton,
  ResourceCTAButtonContainer,
  ResourceCTAButtonText,
  ResourcePermissionsDisclaimerText,
  ResourcePermissionsDisclaimerTextContainer,
  ResourcePermissionsDisclaimerWrapper,
} from 'app/components/Resources/ResourceDetail/styled';
import {
  CloseIconSmall,
  ExternalLinkIcon,
  FolderIcon,
  InfoIcon,
  PinIcon,
  Spinner,
} from 'app/components/shared/icons';
import AddPhotoIcon from 'app/components/shared/icons/add-photo';
import CheckIcon from 'app/components/shared/icons/check-icon';
import { FormikProps, useFormikContext } from 'formik';
import { getImageInfo } from 'services/app';
import { ThemeProvider } from 'styled-components';
import { singularOrPlural } from 'utils';

import {
  FileInputButton,
  FileInputButtonText,
  FileInputContainer,
  FileInputError,
  FileInputErrorContainer,
  FileInputImageActionButton,
  FileInputImageActionsContainer,
  FileInputImageContainer,
  FileInputImagesContainer,
  FileInputImagesMainImage,
  FileInputImagesMainImageContainer,
  FileInputImagesOtherImage,
  FileInputImagesWrapper,
  FileInputInlineCTAContainer,
  FileInputInlineCTAIconContainer,
  FileInputInlineCTAImageContainer,
  FileInputInlineCTAName,
  FileInputInlineCTARemoveIconContainer,
  FileInputInlineCTAWrapper,
  FileInputSubtitle,
  FileInputWrapper,
} from './styled';

export interface CustomError {
  [ErrorCode.FileInvalidType]?: string;
  [ErrorCode.FileTooLarge]?: string;
  [ErrorCode.FileTooSmall]?: string;
  [ErrorCode.TooManyFiles]?: string;
}

export interface FileInputFile {
  filename: string;
  base64: string;
  content_type: string;
}

export interface FileInputProps extends FormikProps<any>, DropzoneOptions {
  name: string;
  additionalFilesName?: string;
  subtitle?: string;
  customErrors?: CustomError;
  maxFiles?: number;
  prepopulatedFiles?: string[];
}

export default function FileInput(props: FileInputProps) {
  const [currentFiles, setCurrentFiles] = useState<FileInputFile[]>([]);
  const [overflowFiles, setOverflowFiles] = useState<string[]>([]);
  const fetchedFiles = useRef<Set<string>>(new Set());
  const finishedFetching = useRef<boolean>(false);
  const formikContext = useFormikContext();
  const isLoading = useMemo<boolean>(() => {
    return (
      props.prepopulatedFiles?.length > 0 && currentFiles.length === 0 && !finishedFetching.current
    );
  }, [props.prepopulatedFiles, currentFiles.length]);

  const getFileFromUrl = async (url: string) => {
    const response = await getImageInfo(url);
    let base64 = response.data.base64;
    const content_type =
      Object.keys(props.accept)[0] === 'application/pdf'
        ? 'application/pdf'
        : response.data.content_type;

    if (content_type === 'application/pdf') {
      base64 = `data:${content_type};base64,${response.data.base64.split(',')[1]}`;
    }

    return {
      base64: base64,
      filename: response.data.filename,
      content_type: content_type,
    };
  };

  useEffect(() => {
    if (props.prepopulatedFiles?.length > 0 && currentFiles.length === 0) {
      Promise.all(
        props.prepopulatedFiles.map((file) => {
          if (
            typeof file === 'object' &&
            file &&
            file['base64'] &&
            file['filename'] &&
            file['content_type']
          ) {
            return {
              base64: file['base64'],
              filename: file['filename'],
              content_type:
                Object.keys(props.accept)[0] === 'application/pdf'
                  ? 'application/pdf'
                  : file['content_type'],
            };
          }

          if (fetchedFiles.current.has(file)) return;
          fetchedFiles.current.add(file);

          return getFileFromUrl(file);
        })
      ).then((files) => {
        setCurrentFiles(files.filter((file) => file !== undefined));
        finishedFetching.current =
          files.filter((file) => file !== undefined).length === props.prepopulatedFiles.length;
      });
    }
  }, [props.prepopulatedFiles]);

  const maxFiles = props.maxFiles || 1;
  const isAtLimit = currentFiles.length >= maxFiles;

  const canUploadImages = useMemo<boolean>(() => {
    return Object.keys(props.accept).some((type) => type.includes('image'));
  }, [props.accept]);

  const handleImagesUploadOnDrop = (acceptedFiles: File[], dzRejections: FileRejection[]) => {
    const remainingSlots = Math.max(0, maxFiles - currentFiles.length);

    const willAccept = acceptedFiles.slice(0, remainingSlots);
    const overflowByCount = acceptedFiles.slice(remainingSlots);

    const tooManyFromDropzone = dzRejections
      .filter((r) => r.errors.some((e) => e.code === ErrorCode.TooManyFiles))
      .map((r) => r.file);

    const overflowMap = new Map<string, File>();
    [...overflowByCount, ...tooManyFromDropzone].forEach((f) => {
      if (f) overflowMap.set(f.name, f);
    });
    const overflow = Array.from(overflowMap.values());

    if (overflow.length > 0) {
      formikContext.setFieldTouched(props.name, true);
      setOverflowFiles(overflow.map((f) => `${f.name}`));
    }

    const newFiles: FileInputFile[] = [];
    let filesProcessed = 0;

    willAccept.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        const fileData = {
          base64: reader.result as string,
          filename: file.name,
          content_type: file.type,
        };
        newFiles.push(fileData);
        filesProcessed++;

        if (filesProcessed === willAccept.length) {
          const updatedFiles =
            !canUploadImages && maxFiles === 1 ? newFiles : [...currentFiles, ...newFiles];

          formikContext.setFieldValue(props.name, updatedFiles);
          setCurrentFiles(updatedFiles);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const handleFilesUploadOnDrop = (acceptedFiles: File[], dzRejections: FileRejection[]) => {
    const reader = new FileReader();
    reader.onload = () => {
      const fileData = {
        base64: reader.result as string,
        filename: acceptedFiles[0].name,
        content_type: acceptedFiles[0].type,
      };
      setCurrentFiles([fileData]);
      formikContext.setFieldValue(props.name, [fileData]);
    };
    reader.readAsDataURL(acceptedFiles[0]);
  };

  const { getRootProps, getInputProps, fileRejections } = useDropzone({
    ...props,
    maxFiles: Infinity,
    multiple: true,
    disabled: isAtLimit && canUploadImages,
    onFileDialogOpen() {
      setOverflowFiles([]);
    },
    onFileDialogCancel() {
      formikContext.setFieldTouched(props.name, true);
      formikContext.setFieldValue(props.name, currentFiles);
    },
    onDrop(acceptedFiles, dzRejections) {
      if (canUploadImages) {
        handleImagesUploadOnDrop(acceptedFiles, dzRejections);
      } else {
        handleFilesUploadOnDrop(acceptedFiles, dzRejections);
      }
    },
  });

  const handleRemoveFile = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    indexToRemove: number
  ) => {
    e.preventDefault();
    e.stopPropagation();
    setOverflowFiles([]);
    const updatedFiles = currentFiles.filter((_, index) => index !== indexToRemove);
    formikContext.setFieldValue(props.name, updatedFiles);
    setCurrentFiles(updatedFiles);
  };

  const acceptedFilesText = useMemo<string>(() => {
    const acceptedFiles = [];

    Object.values(props.accept).forEach((type) => {
      acceptedFiles.push(...type);
    });

    return acceptedFiles.join(' / ');
  }, [props.accept]);

  const handlePinFile = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    const updatedFiles = [...currentFiles];
    updatedFiles.splice(index, 1);
    updatedFiles.unshift(currentFiles[index]);
    setCurrentFiles(updatedFiles);
    formikContext.setFieldValue(props.name, updatedFiles);
  };

  const privacyStatus = useMemo<string>(() => {
    if (props.values['internal_share_status'] === 'open') return 'Viewable by the Collective';
    if (props.values['internal_share_status'] === 'blinded')
      return 'Blinded details for the Collective';

    return null;
  }, [props.values['internal_share_status']]);

  const clientShareStatus = useMemo<string>(() => {
    if (props.values['external_client_share_status'] === 'granted')
      return 'Permission to share externally';
    if (props.values['external_client_share_status'] === 'denied')
      return 'No permission to share externally';
    if (props.values['external_client_share_status'] === 'unknown')
      return 'Client permission pending';

    return null;
  }, [props.values['external_client_share_status']]);

  useEffect(() => {
    if (currentFiles.length > 0) {
      formikContext.setFieldValue(props.name, [currentFiles[0]]);
      if (props.additionalFilesName && currentFiles.length > 1) {
        formikContext.setFieldValue(props.additionalFilesName, currentFiles.slice(1));
      }
    }
  }, [currentFiles]);

  const showErrors = useMemo<boolean>(() => {
    return (
      fileRejections.length > 0 ||
      (props.getFieldMeta(props.name).error && !!props.touched[props.name]) ||
      overflowFiles.length > 0
    );
  }, [fileRejections, props.getFieldMeta(props.name).error, props.touched, overflowFiles]);

  if (isLoading) {
    return <FileInput.Skeleton {...props} canUploadImages={canUploadImages} />;
  }

  return (
    <ThemeProvider
      theme={{
        isError: showErrors,
        isSuccess: currentFiles.length > 0,
      }}
    >
      <FileInputWrapper>
        <input type="hidden" name={props.name} />
        {showErrors ? (
          <FileInputErrorContainer>
            {fileRejections
              .filter((rejection) => rejection.errors[0]?.code !== ErrorCode.TooManyFiles)
              .map((rejection) => (
                <FileInputError key={rejection.errors[0]?.code}>
                  {rejection.file.name}:{' '}
                  {rejection.errors[0]?.code === ErrorCode.FileTooLarge
                    ? `File is larger than ${Math.round(props.maxSize / (1024 * 1024))}MB`
                    : rejection.errors[0]?.message}
                </FileInputError>
              ))}
            {props.getFieldMeta(props.name).error &&
              fileRejections.length === 0 &&
              overflowFiles.length === 0 && (
                <FileInputError>{props.getFieldMeta(props.name).error}</FileInputError>
              )}
            {overflowFiles.map((file) => (
              <FileInputError key={file}>
                Cannot upload {file} — exceeds maximum number of files
              </FileInputError>
            ))}
          </FileInputErrorContainer>
        ) : null}
        <FileInputContainer {...getRootProps()}>
          <input type="text" {...getInputProps()} />
          {currentFiles.length > 0 && canUploadImages ? (
            <FileInputImagesWrapper>
              {currentFiles.slice(0, 1).map((file, index) => (
                <FileInputImagesMainImageContainer key={index}>
                  {privacyStatus || clientShareStatus ? (
                    <ResourcePermissionsDisclaimerWrapper>
                      <ResourcePermissionsDisclaimerTextContainer>
                        <ResourcePermissionsDisclaimerText>
                          {clientShareStatus}
                        </ResourcePermissionsDisclaimerText>
                      </ResourcePermissionsDisclaimerTextContainer>
                      <InfoIcon width={24} height={24} fill="#ffffff" />
                    </ResourcePermissionsDisclaimerWrapper>
                  ) : null}
                  <ResourceCTAButtonContainer>
                    <ResourceCTAButton>
                      <ResourceCTAButtonText>OPEN</ResourceCTAButtonText>
                      <ExternalLinkIcon width={21} height={21} fill="var(--color-primary)" />
                    </ResourceCTAButton>
                  </ResourceCTAButtonContainer>
                  <FileInputImagesMainImage src={file?.base64} />
                  <FileInputImageActionsContainer>
                    <FileInputImageActionButton onClick={(e) => handleRemoveFile(e, 0)}>
                      <CloseIconSmall width={13} height={13} fill="#ffffff" />
                    </FileInputImageActionButton>
                  </FileInputImageActionsContainer>
                </FileInputImagesMainImageContainer>
              ))}
              <FileInputImagesContainer>
                {currentFiles.slice(1).map((file, index) => (
                  <FileInputImageContainer key={index}>
                    <FileInputImagesOtherImage src={file?.base64} />
                    <FileInputImageActionsContainer>
                      <FileInputImageActionButton onClick={(e) => handlePinFile(e, index + 1)}>
                        <PinIcon width={19} height={19} fill="#ffffff" />
                      </FileInputImageActionButton>
                      <FileInputImageActionButton onClick={(e) => handleRemoveFile(e, index + 1)}>
                        <CloseIconSmall width={13} height={13} fill="#ffffff" />
                      </FileInputImageActionButton>
                    </FileInputImageActionsContainer>
                  </FileInputImageContainer>
                ))}
              </FileInputImagesContainer>
              <FileInputSubtitle>
                {!isAtLimit
                  ? `You can add ${maxFiles - currentFiles.length} more images or GIFs.`
                  : 'Maximum files reached'}
              </FileInputSubtitle>
            </FileInputImagesWrapper>
          ) : null}
          {currentFiles.length > 0 && !canUploadImages ? (
            <FileInputInlineCTAWrapper>
              <FileInputInlineCTAImageContainer>
                <FileInputInlineCTAIconContainer>
                  <FolderIcon />
                </FileInputInlineCTAIconContainer>
              </FileInputInlineCTAImageContainer>
              <FileInputInlineCTAContainer>
                <CheckIcon width={48} height={48} fill="#3E8E40" background="none" />
                <FileInputInlineCTAName>{currentFiles[0]?.filename}</FileInputInlineCTAName>
                <FileInputInlineCTARemoveIconContainer onClick={(e) => handleRemoveFile(e, 0)}>
                  <CloseIconSmall width={7} height={7} fill="var(--color-data-alumni-background)" />
                </FileInputInlineCTARemoveIconContainer>
              </FileInputInlineCTAContainer>
            </FileInputInlineCTAWrapper>
          ) : null}
          <FileInputButton isAtLimit={isAtLimit && canUploadImages}>
            <AddPhotoIcon fill="var(--color-primary)" fillOpacity="1" />
            <FileInputButtonText>
              {isAtLimit && !canUploadImages ? `Change file` : `Upload ${acceptedFilesText}`}
            </FileInputButtonText>
          </FileInputButton>
          {props.subtitle ? <FileInputSubtitle>{props.subtitle}</FileInputSubtitle> : null}
        </FileInputContainer>
      </FileInputWrapper>
    </ThemeProvider>
  );
}

interface FileInputSkeletonProps extends FileInputProps {
  canUploadImages: boolean;
}

FileInput.Skeleton = (props: FileInputSkeletonProps) => {
  const amountOfFiles = props.prepopulatedFiles?.length || 0;

  return (
    <FileInputWrapper>
      <FileInputContainer>
        <FileInputImagesWrapper>
          {amountOfFiles > 0 ? (
            <FileInputImagesMainImageContainer>
              <SkeletonItem style={{ width: 563, height: 516, borderRadius: '8px' }} animated />
            </FileInputImagesMainImageContainer>
          ) : null}
          {amountOfFiles > 1 ? (
            <FileInputImagesContainer>
              {Array.from({ length: amountOfFiles - 1 }).map((_, index) => (
                <FileInputImageContainer key={index}>
                  <SkeletonItem style={{ width: 178, height: 54, borderRadius: '8px' }} animated />
                </FileInputImageContainer>
              ))}
            </FileInputImagesContainer>
          ) : null}
        </FileInputImagesWrapper>
      </FileInputContainer>
    </FileInputWrapper>
  );
};
